import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { usePageTitle } from "@/hooks/use-page-title";
import { useRoute, useLocation } from "wouter";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Heart,
  Calendar,
  Building2,
  DollarSign,
  Share2,
  AlertCircle,
  CheckCircle,
  Clock,
  Users,
  Ambulance,
} from "lucide-react";
import { format } from "date-fns";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import healthWalletLogo from "@assets/generated_images/healthwallet_shield_heart_logo.png";

interface EmergencyPublicData {
  id: string;
  patientName: string;
  serviceDescription: string;
  serviceType: string;
  serviceDate: string;
  totalCost: number;
  amountFunded: number;
  status: string;
  urgencyLevel: string;
  shareToken: string;
  partnerName: string | null;
  owner: { firstName: string; lastName: string | null } | null;
  wallet: {
    id: string;
    balance: number;
    goalAmount: number;
    totalContributions: number;
    shareToken: string | null;
  } | null;
  createdAt: string;
}

const SUGGESTED_AMOUNTS = [2500, 5000, 10000, 25000]; // in cents: $25, $50, $100, $250

function formatCents(cents: number): string {
  return (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

export default function EmergencyFundraising() {
  usePageTitle("Emergency Care Community Support", "Support emergency healthcare funding for community members in need through HealthWallet.");
  const { t } = useTranslation();
  const { toast } = useToast();
  const [, params] = useRoute("/emergency/:shareToken");
  const [, setLocation] = useLocation();
  const shareToken = params?.shareToken;

  const searchParams = new URLSearchParams(window.location.search);
  const showSuccess = searchParams.get("success") === "true";

  const [customAmount, setCustomAmount] = useState("");
  const [selectedAmount, setSelectedAmount] = useState<number | null>(null);
  const [contributorName, setContributorName] = useState("");
  const [contributorEmail, setContributorEmail] = useState("");
  const [isContributing, setIsContributing] = useState(false);

  const { data: requestData, isLoading, error } = useQuery<EmergencyPublicData>({
    queryKey: ["/api/public/emergency-requests", shareToken],
    queryFn: async () => {
      const res = await fetch(`/api/public/emergency-requests/${shareToken}`);
      if (!res.ok) throw new Error("Request not found");
      return res.json();
    },
    enabled: !!shareToken,
  });

  const handleContribute = async () => {
    const amount = selectedAmount || Math.round(parseFloat(customAmount) * 100);
    if (!amount || amount <= 0) {
      toast({ title: "Please select or enter an amount", variant: "destructive" });
      return;
    }

    setIsContributing(true);
    try {
      const res = await apiRequest("POST", `/api/public/emergency-requests/${shareToken}/contribute`, {
        amount,
        contributorName: contributorName || "Anonymous",
        contributorEmail,
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    } finally {
      setIsContributing(false);
    }
  };

  const shareLink = () => {
    const url = window.location.href.split("?")[0];
    navigator.clipboard.writeText(url);
    toast({ title: t("emergency.linkCopied", "Link copied to clipboard") });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-muted-foreground">{t("common.loading", "Loading...")}</div>
      </div>
    );
  }

  if (error || !requestData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="max-w-md w-full mx-4">
          <CardContent className="pt-6 text-center space-y-4">
            <AlertCircle className="h-12 w-12 mx-auto text-muted-foreground" />
            <h2 className="text-xl font-semibold">{t("emergency.notFound", "Request Not Found")}</h2>
            <p className="text-muted-foreground">
              {t("emergency.notFoundDesc", "This emergency care community support page may have been removed or the link is invalid.")}
            </p>
            <Button onClick={() => setLocation("/")} data-testid="button-go-home">
              {t("emergency.goHome", "Go to Homepage")}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const fundingPercent = requestData.totalCost > 0
    ? Math.min(Math.round((requestData.amountFunded / requestData.totalCost) * 100), 100)
    : 0;
  const remaining = Math.max(requestData.totalCost - requestData.amountFunded, 0);
  const isFullyFunded = remaining <= 0;
  const isClosed = requestData.status === "closed";

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b bg-card">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-3">
            <img src={healthWalletLogo} alt="HealthWallet" className="h-8 w-8" width="1024" height="1024" />
            <span className="font-semibold text-lg">{t("emergency.healthWallet", "HealthWallet")}</span>
          </div>
          <Button variant="outline" onClick={shareLink} data-testid="button-share">
            <Share2 className="h-4 w-4 mr-2" />
            {t("emergency.share", "Share")}
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        {showSuccess && (
          <Card className="border-primary/20">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <CheckCircle className="h-6 w-6 text-primary flex-shrink-0" />
                <div>
                  <p className="font-medium">{t("emergency.thankYou", "Thank you for your contribution!")}</p>
                  <p className="text-sm text-muted-foreground">
                    {t("emergency.contributionReceived", "Your contribution has been received and will help cover this patient's care.")}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid md:grid-cols-3 gap-6">
          <div className="md:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2 mb-2">
                  <Badge variant={requestData.urgencyLevel === "critical" ? "destructive" : "default"}>
                    <Ambulance className="h-3 w-3 mr-1" />
                    {requestData.urgencyLevel === "critical" ? t("emergency.critical", "Critical") :
                     requestData.urgencyLevel === "urgent" ? t("emergency.urgent", "Urgent") :
                     t("emergency.standard", "Standard")}
                  </Badge>
                  <Badge variant="outline">
                    {requestData.serviceType.replace(/_/g, " ")}
                  </Badge>
                </div>
                <CardTitle className="text-2xl" data-testid="text-patient-name">
                  {t("emergency.helpTitle", "Help {{name}} with emergency care", { name: requestData.patientName })}
                </CardTitle>
                <CardDescription className="text-base">
                  {requestData.serviceDescription}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-4 text-sm text-muted-foreground flex-wrap">
                  {requestData.partnerName && (
                    <div className="flex items-center gap-1">
                      <Building2 className="h-4 w-4" />
                      <span>{requestData.partnerName}</span>
                    </div>
                  )}
                  {requestData.owner && (
                    <div className="flex items-center gap-1" data-testid="text-invited-by">
                      <Users className="h-4 w-4" />
                      <span>
                        {t("emergency.invitedBy", "Invited by")}{" "}
                        {requestData.owner.firstName}
                        {requestData.owner.lastName ? ` ${requestData.owner.lastName}` : ""}
                      </span>
                    </div>
                  )}
                  <div className="flex items-center gap-1">
                    <Calendar className="h-4 w-4" />
                    <span>{format(new Date(requestData.serviceDate), "MMMM d, yyyy")}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Clock className="h-4 w-4" />
                    <span>{t("emergency.created", "Created")} {format(new Date(requestData.createdAt), "MMM d, yyyy")}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">{t("emergency.aboutThisCase", "About This Case")}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-muted-foreground leading-relaxed">
                  {requestData.patientName} {t("emergency.patientStory", "received emergency medical care and needs help covering the costs. Without insurance, the full cost of treatment falls on the patient. Your contribution goes directly to covering their medical expenses through a secure HealthWallet fund.")}
                </p>
                <div className="bg-muted/50 rounded-md p-4 space-y-2">
                  <h4 className="font-medium text-sm">{t("emergency.howFundsUsed", "How funds are used:")}</h4>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-3 w-3 text-primary" />
                      {t("emergency.directPayment", "100% goes directly to medical care costs")}
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-3 w-3 text-primary" />
                      {t("emergency.securePayment", "Secure payment processing via Stripe")}
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-3 w-3 text-primary" />
                      {t("emergency.hospitalVerified", "Verified by the treating hospital")}
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-3 w-3 text-primary" />
                      {t("emergency.remainingPlan", "Any remaining balance converts to a flexible payment schedule")}
                    </li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="space-y-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">{t("emergency.fundingProgress", "Funding Progress")}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="text-center space-y-1">
                  <p className="text-3xl font-bold" data-testid="text-amount-funded">
                    {formatCents(requestData.amountFunded)}
                  </p>
                  <p className="text-muted-foreground">
                    {t("emergency.raisedOf", "raised of")} {formatCents(requestData.totalCost)} {t("emergency.goal", "goal")}
                  </p>
                </div>
                <div className="w-full h-3 bg-muted rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary rounded-full transition-all duration-500"
                    style={{ width: `${fundingPercent}%` }}
                    data-testid="progress-bar"
                  />
                </div>
                <p className="text-center text-sm font-medium">{fundingPercent}% {t("emergency.funded", "funded")}</p>

                {!isFullyFunded && remaining > 0 && (
                  <p className="text-center text-sm text-muted-foreground">
                    {formatCents(remaining)} {t("emergency.stillNeeded", "still needed")}
                  </p>
                )}

                {isFullyFunded && (
                  <div className="text-center p-3 bg-primary/10 rounded-md">
                    <CheckCircle className="h-5 w-5 mx-auto text-primary mb-1" />
                    <p className="font-medium text-primary">{t("emergency.fullyFunded", "Fully Funded!")}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {!isFullyFunded && !isClosed && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">{t("emergency.makeContribution", "Make a Contribution")}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-2">
                    {SUGGESTED_AMOUNTS.map(amount => (
                      <Button
                        key={amount}
                        variant={selectedAmount === amount ? "default" : "outline"}
                        onClick={() => { setSelectedAmount(amount); setCustomAmount(""); }}
                        data-testid={`button-amount-${amount}`}
                      >
                        {formatCents(amount)}
                      </Button>
                    ))}
                  </div>
                  <div>
                    <Label className="text-sm text-muted-foreground">{t("emergency.customAmount", "Custom Amount")}</Label>
                    <div className="relative mt-1">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        type="number"
                        step="0.01"
                        min="1"
                        placeholder="0.00"
                        value={customAmount}
                        onChange={(e) => { setCustomAmount(e.target.value); setSelectedAmount(null); }}
                        className="pl-8"
                        data-testid="input-custom-amount"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm text-muted-foreground">{t("emergency.yourName", "Your Name (optional)")}</Label>
                    <Input
                      value={contributorName}
                      onChange={(e) => setContributorName(e.target.value)}
                      placeholder="Anonymous"
                      className="mt-1"
                      data-testid="input-contributor-name"
                    />
                  </div>
                  <div>
                    <Label className="text-sm text-muted-foreground">{t("emergency.yourEmail", "Your Email (optional)")}</Label>
                    <Input
                      type="email"
                      value={contributorEmail}
                      onChange={(e) => setContributorEmail(e.target.value)}
                      placeholder="email@example.com"
                      className="mt-1"
                      data-testid="input-contributor-email"
                    />
                  </div>
                  <Button
                    className="w-full"
                    size="lg"
                    onClick={handleContribute}
                    disabled={isContributing || (!selectedAmount && !customAmount)}
                    data-testid="button-contribute"
                  >
                    <Heart className="h-4 w-4 mr-2" />
                    {isContributing
                      ? t("emergency.processing", "Processing...")
                      : t("emergency.contributeNow", "Contribute Now")}
                  </Button>
                  <p className="text-xs text-center text-muted-foreground">
                    {t("emergency.securePaymentNote", "Secure payment processing. You'll be redirected to Stripe checkout.")}
                  </p>
                  <div className="mt-3 p-3 rounded-md bg-muted/50 text-xs text-muted-foreground space-y-1" data-testid="text-contribution-disclaimer">
                    <p>Contributions are voluntary financial support processed by Stripe, Inc. HealthWallet does not hold or custody funds. Contributions may not be tax-deductible. Contributors should consult a tax professional regarding any tax implications.</p>
                    <p>By contributing, you agree to our <a href="/terms" className="underline" target="_blank" rel="noopener noreferrer">Terms of Service</a> and <a href="/privacy" className="underline" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.</p>
                  </div>
                </CardContent>
              </Card>
            )}

            {isClosed && (
              <Card>
                <CardContent className="pt-6 text-center space-y-2">
                  <AlertCircle className="h-8 w-8 mx-auto text-muted-foreground" />
                  <p className="font-medium">{t("emergency.closed", "This campaign is closed")}</p>
                  <p className="text-sm text-muted-foreground">
                    {t("emergency.closedDesc", "This emergency care request is no longer accepting contributions.")}
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </main>

      <footer className="border-t mt-12">
        <div className="max-w-4xl mx-auto px-4 py-6 text-center text-sm text-muted-foreground space-y-2">
          <p>{t("emergency.footerNote", "HealthWallet is a community-powered healthcare funding platform serving the Minneapolis-St. Paul area.")}</p>
          <p className="mt-1">{t("emergency.securityNote", "All personal information is securely protected with enterprise-grade encryption.")}</p>
          <p className="text-xs">HealthWallet provides technology services only. All funds are processed and held by Stripe, Inc. <a href="/terms" className="underline">Terms</a> | <a href="/privacy" className="underline">Privacy</a></p>
        </div>
      </footer>
    </div>
  );
}
